import { useTranslation } from 'react-i18next';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { FieldError } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import {
  MANUAL_PAYMENT_METHODS,
  isPaymentRequestMethod,
  paymentMethodLabelKey,
} from '@/lib/manualPaymentMethods';
import { cn } from '@/utils/cn';

interface ManualPaymentFieldsProps {
  idPrefix: string;
  method: string;
  onMethodChange: (method: string) => void;
  methodError?: string;
  otherDetail: string;
  onOtherDetailChange: (value: string) => void;
  otherError?: string;
  paymentDate: string;
  onPaymentDateChange: (value: string) => void;
  dateError?: string;
}

export function ManualPaymentFields({
  idPrefix,
  method,
  onMethodChange,
  methodError,
  otherDetail,
  onOtherDetailChange,
  otherError,
  paymentDate,
  onPaymentDateChange,
  dateError,
}: ManualPaymentFieldsProps) {
  const { t } = useTranslation();
  const methodId = `${idPrefix}-method`;
  const otherId = `${idPrefix}-method-other`;
  const dateId = `${idPrefix}-date`;

  return (
    <>
      <div>
        <label htmlFor={methodId} className={cn('block', FORM_LABEL_TYPE)}>
          {t('invoices.field_payment_method')}
        </label>
        <select
          id={methodId}
          name={methodId}
          required
          aria-invalid={methodError ? true : undefined}
          value={method}
          onChange={(event) => {
            const next = event.target.value;
            onMethodChange(next);
            if (next !== 'other') onOtherDetailChange('');
          }}
          className={cn(
            TYPE.inputValue,
            'mt-1 block w-full rounded-md border bg-ds-card px-3 py-2 text-ds-text shadow-sm focus:border-ds-teal focus:outline-none focus:shadow-ring',
            methodError ? 'border-ds-red' : 'border-ds-field',
          )}
        >
          <option value="" disabled>
            {t('invoices.payment_method_select_placeholder')}
          </option>
          {MANUAL_PAYMENT_METHODS.map((value) => (
            <option key={value} value={value}>
              {t(paymentMethodLabelKey(value))}
            </option>
          ))}
        </select>
        {methodError ? <FieldError>{methodError}</FieldError> : null}
      </div>

      {method === 'other' && (
        <Input
          id={otherId}
          name={otherId}
          label={t('invoices.payment_method_other_label')}
          value={otherDetail}
          onChange={(event) => onOtherDetailChange(event.target.value)}
          placeholder={t('invoices.payment_method_other_placeholder')}
          maxLength={80}
          required
          autoComplete="off"
          error={otherError}
        />
      )}

      {isPaymentRequestMethod(method) ? (
        <p className="text-sm text-ds-muted">
          {t('invoices.mobilepay_request_note')}
        </p>
      ) : (
        <div>
          <DanishDateField
            name={dateId}
            label={t('invoices.payment_date')}
            value={paymentDate}
            onChange={onPaymentDateChange}
            required
          />
          {dateError ? <FieldError>{dateError}</FieldError> : null}
        </div>
      )}
    </>
  );
}
